import type {
  PlaybookCriterionNode,
  PlaybookId,
} from "@repo/app-types/entities/playbook";
import type { FC } from "react";
import type { PlaybookCriterionTopExample } from "../../api/entities/performancePlaybook.js";
import { appPaths } from "../../app/appPaths.js";
import {
  CriteriaScoreCard,
  CriteriaScoreCardSkeleton,
} from "./criteriaScoreCard.js";

const TEAM_EXAMPLES_LOADING_SKELETON_COUNT = 3;

export const CriterionTeamExamplesSkeleton: FC = () => {
  return (
    <div className="flex flex-col gap-3">
      <div className="text-sm font-semibold text-foreground">
        Top examples from the team
      </div>
      <div className="overflow-x-auto px-1 py-1">
        <div className="flex min-w-max gap-3 pr-1">
          {Array.from({ length: TEAM_EXAMPLES_LOADING_SKELETON_COUNT }).map(
            (_, index) => (
              <CriteriaScoreCardSkeleton key={`team-example-${index}`} />
            ),
          )}
        </div>
      </div>
    </div>
  );
};

export interface CriterionTeamExamplesViewProps {
  playbookId: PlaybookId;
  criterion: PlaybookCriterionNode;
  medianAverageScore: number | undefined;
  sortedGradings: readonly PlaybookCriterionTopExample[];
  isLoading: boolean;
  isError: boolean;
}

export const CriterionTeamExamplesView: FC<CriterionTeamExamplesViewProps> = (
  props,
) => {
  const {
    playbookId,
    criterion,
    medianAverageScore,
    sortedGradings,
    isLoading,
    isError,
  } = props;

  if (isLoading) {
    return <CriterionTeamExamplesSkeleton />;
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-semibold text-foreground">
        Top examples from the team
      </div>
      <div className="overflow-x-auto px-1 py-1">
        <div className="flex min-w-max gap-3 pr-1">
          {isError ? (
            <div className="text-sm text-destructive">
              Unable to load team examples.
            </div>
          ) : sortedGradings.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No top-rated examples yet.
            </div>
          ) : (
            sortedGradings.map(({ grading, teammate }) => (
              <CriteriaScoreCard
                key={grading.id}
                to={appPaths.teammateCallCriterion.build({
                  playbookId,
                  teammateId: grading.teammate_id,
                  gradingId: grading.id,
                  criterionId: criterion.id,
                })}
                grading={grading}
                criterion={criterion}
                teammate={teammate}
                teammateAverageScore={teammate.average_score}
                medianAverageScore={medianAverageScore}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};
